/*
 * Trophy form-tracking observer v5.
 *
 * Emits canonical GA4 dataLayer events for the four mounted HulkApps
 * sales-lead forms. Non-sales and closed-offer surfaces are intentionally
 * excluded per the 2026-06-20 form coverage decision.
 *
 * v5 keeps the v3 child-list observer, but also handles the current
 * HulkApps runtime shape where success alerts are mounted hidden at page load
 * and same-form status blocks become rendered after the Ajax submit.
 */
(function () {
  'use strict';

  if (window.__trophyFormObserverV5Installed) return;
  window.__trophyFormObserverV5Installed = true;
  window.__trophyFormObserverV4Installed = true;

  var FORM_UUID_TO_EVENT = {
    'uIPfEQQ_vIKkm1AkFV_oSw': 'wedding_contact_form',
    '4CE3kmFeOGzKfmMsJDLt8Q': 'custom_event_contact_form',
    'iSg6Hiszy01uVi53_CME0Q': 'contact_us_form',
    'qjvd7-klOaFr7l6PZCBR5g': 'custom_products_form'
  };

  var FORM_UUID_TO_CONTEXT = {
    'uIPfEQQ_vIKkm1AkFV_oSw': {
      form_type: 'wedding',
      lead_type: 'wedding'
    },
    '4CE3kmFeOGzKfmMsJDLt8Q': {
      form_type: 'custom_event',
      lead_type: 'custom_event'
    },
    'iSg6Hiszy01uVi53_CME0Q': {
      form_type: 'contact',
      lead_type: 'general_contact'
    },
    'qjvd7-klOaFr7l6PZCBR5g': {
      form_type: 'custom_products',
      lead_type: 'custom_products'
    }
  };

  var THANK_YOU_PATH_TO_UUID = {
    '/pages/thank-you-for-your-custom-cupcake-ideas': '4CE3kmFeOGzKfmMsJDLt8Q',
    '/pages/thank-you-custom-products-form': 'qjvd7-klOaFr7l6PZCBR5g'
  };

  var OCCASION_PATHS = [
    { match: /^\/pages\/weddings\/?$/, occasion: 'wedding' },
    { match: /^\/pages\/custom-events\/?$/, occasion: 'custom_event' },
    { match: /^\/pages\/custom-corporate-treats\/?$/, occasion: 'corporate' },
    { match: /^\/pages\/custom-logo-cupcakes\/?$/, occasion: 'corporate' },
    {
      match: /^\/pages\/thank-you-for-your-custom-cupcake-ideas\/?$/,
      occasion: 'custom_event'
    },
    {
      match: /^\/pages\/thank-you-custom-products-form\/?$/,
      occasion: 'custom_products'
    }
  ];

  var TAG13_COVERED_UUIDS = {
    'uIPfEQQ_vIKkm1AkFV_oSw': true,
    'iSg6Hiszy01uVi53_CME0Q': true
  };

  /*
   * Hosts whose referral to a thank-you page is treated as a genuine submit.
   *
   * `formbuilder.hulkapps.com` is the HulkApps form host. Its referral is
   * strong evidence of a real submit, because that host links to a thank-you
   * page only on submit — but note this is ALIGNMENT, not a recovery fix.
   *
   * READ THIS BEFORE CHANGING THE THANK-YOU GATE BELOW: on the thank-you pages
   * this observer is NOT the live path. GTM Tag 29 ("cHTML - Custom products
   * thank-you canonical event bridge", All Pages) pushes `custom_products_form`
   * itself, with its own referrer check that has always allowlisted
   * formbuilder.hulkapps.com. Event-scoped GA4 data over 90d shows
   * `trophy_lead_submit` firing 42x with a hulkapps referrer and 2x with an
   * EMPTY referrer — both values this observer's gate rejects — which is how we
   * know GTM, not this file, is producing them. An earlier reading of the same
   * symptom as "~26% of submits are being dropped by this allowlist" was WRONG
   * and is retracted; the events were firing the whole time.
   *
   * CONSEQUENCE: changes to `detectThankYouPagePath` are largely inert for the
   * thank-you pages. The submit-marker check below is defensive alignment; the
   * gate that actually decides is in Tag 29.
   *
   * SEPARATE, STILL OPEN: hulkapps arrivals start a NEW GA4 referral session,
   * so google/cpc attribution is broken regardless of which path fires the
   * event. That needs formbuilder.hulkapps.com in GA4's unwanted-referrals
   * list; nothing in this file fixes attribution.
   */
  var ALLOWED_REFERRER_HOSTS = [
    'trophycupcakes.com',
    'shoptrophy.myshopify.com',
    'formbuilder.hulkapps.com'
  ];

  /*
   * Submit marker. The HulkApps forms redirect to their thank-you page via
   * `after_submit_url`; appending `?src=form` there makes the marker something
   * only a real submit can produce. This is strictly better than the referrer
   * check it supplements: a query param survives privacy browsers,
   * `Referrer-Policy: no-referrer`, and in-app/iOS webviews, all of which strip
   * the referrer and cause genuine submits to go uncounted.
   *
   * TRANSITIONAL: the gate below accepts marker OR allowed referrer, so this is
   * a no-op until `after_submit_url` is updated in the HulkApps admin. Once the
   * marker is live and volume is confirmed, drop the referrer branch.
   */
  var SUBMIT_MARKER_PARAM = 'src';
  var SUBMIT_MARKER_VALUE = 'form';
  var SESSION_KEY_PREFIX = 'trophy_form_observer_fired_';
  var HULK_FORM_CLASS_PREFIX = 'hulk_form_';
  var VISITOR_CONSENT_EVENT = 'visitorConsentCollected';
  var LEGACY_TRACKING_CONSENT_EVENT = 'trackingConsentAccepted';
  var PICKUP_DELIVERY_MODES = ['pickup', 'delivery', 'shipping'];
  var SUCCESS_SELECTORS = [
    '.alert.alert-success',
    '.after_form_submit .form_generater_form_div[role="status"]',
    '.after_form_submit [aria-live]',
    '.form_generater_form_div[role="status"][aria-live]'
  ];
  var POLL_INTERVAL_MS = 250;
  var POLL_TIMEOUT_MS = 12000;
  var firedUuids = new Set();
  var firedLeadSubmitUuids = new Set();
  var pendingUuids = new Set();
  var pollTimers = {};
  var pollStartedAt = {};
  var queuedPayloads = [];
  var consentListenerBound = false;

  function hasSubmitMarker() {
    try {
      var params = new URLSearchParams(window.location.search);
      return params.get(SUBMIT_MARKER_PARAM) === SUBMIT_MARKER_VALUE;
    } catch (_) {
      return false;
    }
  }

  /*
   * Parses the host rather than substring-matching the whole referrer string.
   * The previous `indexOf` form matched the allowlist ANYWHERE in the referrer,
   * so https://attacker.example/?x=trophycupcakes.com passed as first-party.
   * Matches the apex exactly or any subdomain of it, so www.trophycupcakes.com
   * still resolves as first-party the way it did before.
   */
  function isAllowedReferrer(referrer) {
    if (!referrer) return false;

    var host;

    try {
      host = new URL(referrer).hostname.toLowerCase();
    } catch (_) {
      return false;
    }

    if (!host) return false;

    for (var i = 0; i < ALLOWED_REFERRER_HOSTS.length; i++) {
      var allowed = ALLOWED_REFERRER_HOSTS[i];

      if (host === allowed) return true;
      if (host.slice(-(allowed.length + 1)) === '.' + allowed) return true;
    }

    return false;
  }

  function isRenderedNode(node) {
    if (!node) return false;

    if (typeof node.getClientRects === 'function' && node.getClientRects().length === 0) {
      return false;
    }

    if (typeof node.getBoundingClientRect === 'function') {
      var rect = node.getBoundingClientRect();

      if (!rect || (rect.width === 0 && rect.height === 0)) return false;
    }

    return true;
  }

  function findHulkFormUuid(node) {
    var el = node;

    while (el && el.nodeType === 1) {
      if (el.classList) {
        if (el.classList.contains('pxFormGenerator') && FORM_UUID_TO_EVENT[el.id]) {
          return el.id;
        }

        for (var i = 0; i < el.classList.length; i++) {
          var cls = el.classList[i];

          if (cls.indexOf(HULK_FORM_CLASS_PREFIX) === 0) {
            return cls.slice(HULK_FORM_CLASS_PREFIX.length);
          }
        }
      }

      el = el.parentNode;
    }

    return null;
  }

  function findRenderedSuccessAlertForUuid(uuid) {
    if (!uuid || !document.querySelectorAll) return null;

    var alerts = document.querySelectorAll(SUCCESS_SELECTORS.join(','));

    for (var i = 0; i < alerts.length; i++) {
      if (
        findHulkFormUuid(alerts[i]) === uuid &&
        isRenderedNode(alerts[i]) &&
        hasMeaningfulSuccessText(alerts[i])
      ) {
        return alerts[i];
      }
    }

    return null;
  }

  function hasMeaningfulSuccessText(node) {
    var text = ((node && (node.textContent || node.getAttribute('aria-label'))) || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    return (
      text.indexOf('thank you') !== -1 ||
      text.indexOf('thanks') !== -1 ||
      text.indexOf('received your inquiry') !== -1 ||
      text.indexOf('marvelous') !== -1
    );
  }

  function compact(payload) {
    var result = {};

    Object.keys(payload).forEach(function (key) {
      if (payload[key] !== undefined && payload[key] !== null && payload[key] !== '') {
        result[key] = payload[key];
      }
    });

    return result;
  }

  function normalizePath(pathName) {
    var path = String(pathName || '/').split('?')[0].split('#')[0];
    return path.indexOf('/') === 0 ? path : '/' + path;
  }

  function normalizeToken(value) {
    if (value === undefined || value === null) return undefined;

    return String(value)
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function normalizeHandle(value) {
    if (!value) return undefined;

    try {
      return decodeURIComponent(String(value)).trim().toLowerCase();
    } catch (_) {
      return String(value).trim().toLowerCase();
    }
  }

  function getShopifyMeta() {
    return (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
  }

  function getPageType(pathName) {
    var meta = getShopifyMeta();
    var metaPageType = meta.page && meta.page.pageType;

    if (metaPageType) return normalizeToken(metaPageType);

    var path = normalizePath(pathName);

    if (path === '/') return 'home';
    if (path.indexOf('/collections/') === 0) return 'collection';
    if (path.indexOf('/products/') === 0) return 'product';
    if (path === '/cart') return 'cart';

    return 'page';
  }

  function getLocationSlug(pathName) {
    var match = normalizePath(pathName).match(/^\/pages\/locations-(.+?)\/?$/);
    return match ? normalizeHandle(match[1]) : undefined;
  }

  function getOccasion(pathName) {
    var path = normalizePath(pathName);

    for (var i = 0; i < OCCASION_PATHS.length; i++) {
      if (OCCASION_PATHS[i].match.test(path)) return OCCASION_PATHS[i].occasion;
    }

    return undefined;
  }

  function getActiveZapietMode() {
    if (!document.querySelector) return undefined;

    var activeImage = document.querySelector('#storePickupApp .checkoutMethodImage.active img');
    var activeLabel = normalizeToken(
      activeImage && (activeImage.getAttribute('alt') || activeImage.alt)
    );

    if (activeLabel) {
      for (var i = 0; i < PICKUP_DELIVERY_MODES.length; i++) {
        if (activeLabel.indexOf(PICKUP_DELIVERY_MODES[i]) !== -1) {
          return PICKUP_DELIVERY_MODES[i];
        }
      }
    }

    var activeMethod = document.querySelector('#storePickupApp .checkoutMethod.active');
    var activeMethodClass = normalizeToken(activeMethod && activeMethod.className);

    if (!activeMethodClass) return undefined;

    for (var j = 0; j < PICKUP_DELIVERY_MODES.length; j++) {
      if (activeMethodClass.indexOf(PICKUP_DELIVERY_MODES[j]) !== -1) {
        return PICKUP_DELIVERY_MODES[j];
      }
    }

    return undefined;
  }

  function buildLeadSubmitPageContext(pathName) {
    var path = normalizePath(pathName || window.location.pathname);

    return compact({
      page_type: getPageType(path),
      occasion: getOccasion(path),
      location_slug: getLocationSlug(path),
      pickup_delivery_mode: getActiveZapietMode()
    });
  }

  function getCustomerPrivacy() {
    return window.Shopify ? window.Shopify.customerPrivacy : null;
  }

  function isAnalyticsProcessingAllowed() {
    var customerPrivacy = getCustomerPrivacy();

    if (!customerPrivacy || typeof customerPrivacy.analyticsProcessingAllowed !== 'function') {
      return true;
    }

    try {
      return customerPrivacy.analyticsProcessingAllowed() !== false;
    } catch (_) {
      return true;
    }
  }

  function queuePayload(payload) {
    queuedPayloads.push(payload);

    if (queuedPayloads.length > 20) {
      queuedPayloads = queuedPayloads.slice(-20);
    }
  }

  function flushQueuedPayloads() {
    if (!queuedPayloads.length || !isAnalyticsProcessingAllowed()) return;

    var payloads = queuedPayloads;
    queuedPayloads = [];
    payloads.forEach(function (payload) {
      pushToDataLayer(payload);
    });
  }

  function bindConsentListener() {
    if (consentListenerBound || !document.addEventListener) return;

    consentListenerBound = true;
    document.addEventListener(VISITOR_CONSENT_EVENT, flushQueuedPayloads);
    document.addEventListener(LEGACY_TRACKING_CONSENT_EVENT, flushQueuedPayloads);
  }

  function pushToDataLayer(payload) {
    if (!isAnalyticsProcessingAllowed()) {
      queuePayload(payload);
      bindConsentListener();

      return false;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    return true;
  }

  function shouldDeferToTag13(uuid) {
    return !!(window.__trophyFormListenerInstalled && TAG13_COVERED_UUIDS[uuid]);
  }

  function stopPoll(uuid) {
    if (!pollTimers[uuid]) return;

    window.clearInterval(pollTimers[uuid]);
    delete pollTimers[uuid];
    delete pollStartedAt[uuid];
  }

  function pushCanonicalEvent(uuid, pathName) {
    if (firedUuids.has(uuid)) return;

    var eventName = FORM_UUID_TO_EVENT[uuid];

    if (!eventName) {
      try { console.warn('[trophy-form-observer-v5] unrecognized form_uuid', uuid); } catch (_) {}

      return;
    }

    firedUuids.add(uuid);
    var pushed = pushToDataLayer({
      event: eventName,
      form_uuid: uuid,
      form_app: 'hulkapps_form_builder_1496853'
    });

    try {
      console.info(
        '[trophy-form-observer-v5] ' +
          (pushed ? 'fired ' : 'queued ') +
          eventName +
          ' (path=' +
          pathName +
          ')'
      );
    } catch (_) {}
  }

  function pushLeadSubmitEvent(uuid, pathName) {
    if (firedLeadSubmitUuids.has(uuid)) return;

    var formContext = FORM_UUID_TO_CONTEXT[uuid];

    if (!formContext) return;

    firedLeadSubmitUuids.add(uuid);
    var pageContext = buildLeadSubmitPageContext();
    var pushed = pushToDataLayer(compact({
      event: 'trophy_lead_submit',
      page_type: pageContext.page_type,
      occasion: pageContext.occasion,
      location_slug: pageContext.location_slug,
      pickup_delivery_mode: pageContext.pickup_delivery_mode,
      form_uuid: uuid,
      form_app: 'hulkapps_form_builder_1496853',
      form_type: formContext.form_type,
      lead_type: formContext.lead_type
    }));

    try {
      console.info(
        '[trophy-form-observer-v5] ' +
          (pushed ? 'fired ' : 'queued ') +
          'trophy_lead_submit (path=' +
          pathName +
          ')'
      );
    } catch (_) {}
  }

  function maybeFireForUuid(uuid, pathName) {
    if (!pendingUuids.has(uuid)) return false;
    if (!findRenderedSuccessAlertForUuid(uuid)) return false;

    pendingUuids.delete(uuid);
    stopPoll(uuid);
    pushLeadSubmitEvent(uuid, pathName);

    if (shouldDeferToTag13(uuid)) {
      try {
        console.info('[trophy-form-observer-v5] deferred ' + FORM_UUID_TO_EVENT[uuid] + ' to Tag 13');
      } catch (_) {}

      return false;
    }

    pushCanonicalEvent(uuid, pathName);

    return true;
  }

  function startSuccessPoll(uuid) {
    if (pollTimers[uuid]) return;

    pollStartedAt[uuid] = Date.now();
    pollTimers[uuid] = window.setInterval(function () {
      if (!pendingUuids.has(uuid)) {
        stopPoll(uuid);
        return;
      }

      if (maybeFireForUuid(uuid, 'poll')) return;

      if (Date.now() - pollStartedAt[uuid] > POLL_TIMEOUT_MS) {
        pendingUuids.delete(uuid);
        stopPoll(uuid);
      }
    }, POLL_INTERVAL_MS);
  }

  function markPendingForm(node) {
    var uuid = findHulkFormUuid(node);

    if (!uuid || !FORM_UUID_TO_EVENT[uuid]) return;

    pendingUuids.add(uuid);
    startSuccessPoll(uuid);
    maybeFireForUuid(uuid, 'interaction');
  }

  function detectThankYouPagePath() {
    var uuid = THANK_YOU_PATH_TO_UUID[window.location.pathname];

    if (!uuid) return;

    /*
     * window.location.pathname excludes the query string, so the lookup above
     * keeps matching once `?src=form` is appended — no table changes needed.
     */
    if (!hasSubmitMarker() && !isAllowedReferrer(document.referrer || '')) return;

    try {
      var key = SESSION_KEY_PREFIX + uuid;

      if (window.sessionStorage && window.sessionStorage.getItem(key)) return;
      if (window.sessionStorage) window.sessionStorage.setItem(key, '1');
    } catch (_) {}

    pushLeadSubmitEvent(uuid, 'thank-you');
    pushCanonicalEvent(uuid, 'thank-you');
  }

  function handleMutationBatch() {
    pendingUuids.forEach(function (uuid) {
      maybeFireForUuid(uuid, 'mutation');
    });
  }

  function start() {
    detectThankYouPagePath();

    if (!('MutationObserver' in window) || !document.body) return;

    var observer = new MutationObserver(function () {
      try {
        handleMutationBatch();
      } catch (err) {
        try { console.warn('[trophy-form-observer-v5] handler error', err); } catch (_) {}
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
      childList: true,
      subtree: true
    });

    document.addEventListener('submit', function (event) {
      markPendingForm(event.target);
    }, true);

    document.addEventListener('click', function (event) {
      var node = event.target;

      while (node && node.nodeType === 1) {
        if (
          node.tagName === 'BUTTON' ||
          (node.tagName === 'INPUT' && (node.type === 'submit' || node.type === 'button'))
        ) {
          markPendingForm(node);
          return;
        }

        node = node.parentNode;
      }
    }, true);

    try { console.info('[trophy-form-observer-v5] active path=mutation+poll+status'); } catch (_) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
